import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./styles/RestaurantSetupReadiness.css";

const COUNT_LABELS = { employees: "Staff", activeEmployees: "Active staff", tables: "Tables", menuItems: "Menu items" };
const CAPABILITY_LABELS = { cardPayments: "Card payments", pickup: "Pickup", reservations: "Reservations", reporting: "Reporting",
	deviceConfigured: "Device configuration", printerConfigured: "Printer configuration" };
const validId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
const nonempty = (value) => typeof value === "string" && value.trim().length > 0;
const inScope = (value, restaurantId) => value?.restaurantId === undefined || value.restaurantId === restaurantId;
const validSnapshot = (value, restaurantId) => {
	const setup = value?.setup;
	return value?.success === true && inScope(value, restaurantId) && inScope(setup, restaurantId) &&
		Object.keys(COUNT_LABELS).every((key) => Number.isSafeInteger(value.counts?.[key]) && value.counts[key] >= 0) &&
		value.counts.activeEmployees <= value.counts.employees && setup?.version === 1 &&
		typeof setup.ready === "boolean" && typeof setup.canActivateCheckout === "boolean" &&
		Object.keys(CAPABILITY_LABELS).every((key) => typeof setup.capabilities?.[key] === "boolean") &&
		Array.isArray(setup.steps) && setup.steps.length > 0 && setup.steps.every((step) => step &&
			nonempty(step.id) && nonempty(step.label) && typeof step.message === "string" &&
			typeof step.ready === "boolean" && typeof step.required === "boolean" && typeof step.action === "string") &&
		new Set(setup.steps.map((step) => step.id)).size === setup.steps.length &&
		(!setup.ready || setup.steps.every((step) => !step.required || step.ready));
};
const canActivate = (setup) => setup?.canActivateCheckout === true &&
	(setup.capabilities.cardPayments === false || setup.steps.some((step) => step.id === "checkout" && step.ready === false));
const checkoutEnabled = (setup) => setup.capabilities.cardPayments === true &&
	!setup.steps.some((step) => step.id === "checkout" && step.ready === false);
const readError = (error) => {
	const code = String(error?.code || "").replace(/^functions\//, "");
	if (["unauthenticated", "permission-denied"].includes(code)) return "Scerv admin access is required. Sign in with an authorized support account.";
	if (code === "not-found") return "Restaurant or setup endpoint unavailable. Readiness is unknown.";
	return "Could not load valid setup readiness. Refresh to try again.";
};

function SetupAction({ step, restaurantId, onOpenSection }) {
	const section = step.action === "devices" ? "devices" : step.action === "hardware" ? "printers" : null;
	const labels = { devices: "Open Devices & Enrollment", printers: "Open Printers & Drawers" };
	if (section) return onOpenSection ? <button type="button" onClick={() => onOpenSection(section)}>{labels[section]}</button> :
		<span>Advanced settings &gt; {labels[section].replace("Open ", "")}</span>;
	if (step.action === "menu") return <><Link to={`/restaurants/${encodeURIComponent(restaurantId)}/menu`}>Manage menu</Link>
		<span>Restaurant app: Back Office &gt; Menu.</span></>;
	const directions = {
		profile: "Profile: complete restaurant name, address and contact details, then save.",
		employees: "Restaurant app: Back Office > Employees. Add active staff and set their PINs.",
		tables: "Restaurant app: Back Office > Tables. Add the restaurant's tables.",
		reporting: "Restaurant app: Back Office > Reporting settings. Save the timezone and business-day cutoff under Reporting policy.",
		pricing: "Profile: review Tax rate. Ask Scerv support to verify the restaurant's pricing tier and checkout fees.",
		payments: "Restaurant app: Back Office > Payouts. Complete or refresh onboarding for the current payment mode.",
		reservations: "Restaurant app: Back Office > Reservation Settings. Review and save availability.",
		checkout: "Review the checkout status and activation controls below.",
	};
	return <span>{Object.prototype.hasOwnProperty.call(directions, step.action) ? directions[step.action] :
		"Review this setup requirement with Scerv support."}</span>;
}

function ReadinessStatus({ restaurantId, user, environment, onOpenSection }) {
	const [snapshot, setSnapshot] = useState(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const instanceId = useId();
	const generation = useRef({ value: 0 });
	const alive = useRef(false);
	const pending = useRef(false);
	const serviceApp = useRef(functions.app).current;
	const sameScope = useCallback(() => getStaffProvisioningEnvironment() === environment &&
		functions.app === serviceApp && auth.currentUser === user, [environment, serviceApp, user]);
	const current = useCallback((ticket) => alive.current && generation.current.value === ticket && sameScope(), [sameScope]);
	const authorize = useCallback(async (ticket) => {
		if (!current(ticket)) return false;
		const token = await getIdTokenResult(user);
		if (!current(ticket)) return false;
		const role = String(token.claims.role || "");
		if (!canAccessAdminPortal(role) || role !== role.trim()) throw Object.assign(new Error("Denied"), { code: "permission-denied" });
		return true;
	}, [current, user]);
	const finish = useCallback((ticket) => {
		if (!alive.current || generation.current.value !== ticket) return;
		pending.current = false; setBusy(false);
		if (!sameScope()) {
			setSnapshot(null); setMessage("");
			setError("Support account or environment changed. Reload this page before continuing.");
		}
	}, [sameScope]);
	const fetchSnapshot = useCallback(async (ticket) => {
		if (!current(ticket)) return null;
		const response = await httpsCallable(functions, "getScervRestaurantSetup")({ restaurantId });
		if (!current(ticket)) return null;
		if (!validSnapshot(response?.data, restaurantId)) throw new Error("Invalid setup response");
		return response.data;
	}, [current, restaurantId]);
	const reload = useCallback(async () => {
		if (pending.current || !sameScope()) return;
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true); setSnapshot(null); setError(""); setMessage("");
		try {
			if (!await authorize(ticket)) return;
			const saved = await fetchSnapshot(ticket);
			if (saved && current(ticket)) setSnapshot(saved);
		} catch (err) {
			if (current(ticket)) setError(readError(err));
		} finally { finish(ticket); }
	}, [authorize, current, fetchSnapshot, finish, sameScope]);
	useEffect(() => {
		const counter = generation.current;
		alive.current = true;
		reload();
		return () => { alive.current = false; counter.value++; pending.current = false; };
	}, [reload]);

	const activate = async () => {
		if (pending.current || !sameScope() || !canActivate(snapshot?.setup)) return;
		if (!window.confirm(`Activate checkout for restaurant ${restaurantId} in ${environment}? This changes checkout configuration. It does not issue a payment or verify hardware.`)) return;
		if (pending.current || !sameScope()) return;
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true); setSnapshot(null); setError(""); setMessage("");
		try {
			if (!await authorize(ticket)) return;
			const response = await httpsCallable(functions, "activateScervRestaurantCheckout")({ restaurantId });
			if (!current(ticket)) return;
			if (response?.data?.success !== true || !inScope(response.data, restaurantId) || !inScope(response.data.setup, restaurantId)) {
				throw new Error("Unconfirmed activation");
			}
			// Re-read authoritative readiness; an activation acknowledgement is not a readiness snapshot.
			const saved = await fetchSnapshot(ticket);
			if (!saved || !current(ticket)) return;
			if (!checkoutEnabled(saved.setup)) throw new Error("Activation not verified");
			setSnapshot(saved); setMessage("Checkout activation confirmed. No payment was issued.");
		} catch (err) {
			if (current(ticket)) {
				setSnapshot(null);
				setError("Checkout activation is unconfirmed or blocked. Refresh readiness before trying again.");
			}
		} finally { finish(ticket); }
	};
	const setup = sameScope() ? snapshot?.setup : null;
	const ready = setup?.ready === true;

	return <section className="restaurant-setup-readiness" aria-labelledby={`${instanceId}-heading`} aria-busy={busy}>
		<div className="restaurant-setup-header">
			<h3 id={`${instanceId}-heading`}>Setup status</h3>
			<span className="neutral-pill">{environment} only</span>
			<button type="button" disabled={busy || !sameScope()} onClick={reload}>Refresh readiness</button>
		</div>
		<p className="restaurant-setup-scope">Restaurant: {restaurantId}</p>
		<p role="status" className={`restaurant-setup-status ${ready ? "is-ready" : "is-pending"}`}>
			{busy ? "Checking setup readiness..." : setup ? ready ? "Setup ready" : "Setup incomplete" : "Readiness unknown"}
		</p>
		{error && <p className="restaurant-error" role="alert">{error}</p>}
		{message && <p className="restaurant-message" role="status">{message}</p>}
		{setup && <>
			<dl className="restaurant-setup-counts">{Object.entries(COUNT_LABELS).map(([key, label]) =>
				<div key={key}><dt>{label}</dt><dd>{snapshot.counts[key]}</dd></div>)}</dl>
			<ul className="restaurant-setup-steps">{setup.steps.map((step) => <li key={step.id}>
				<div className="restaurant-setup-step-status"><strong>{step.label}</strong>
					<span>{step.ready ? "Ready" : "Not ready"} / {step.required ? "Required" : "Optional"}</span></div>
				<div className="restaurant-setup-step-detail"><p>{step.message}</p>
					{!step.ready && <div className="restaurant-setup-step-action"><SetupAction step={step} restaurantId={restaurantId} onOpenSection={onOpenSection} /></div>}
				</div>
			</li>)}</ul>
			<h4>Capabilities</h4>
			<dl className="restaurant-setup-capabilities">{Object.entries(CAPABILITY_LABELS).map(([key, label]) =>
				<div key={key}><dt>{label}</dt><dd>{key.endsWith("Configured") ? setup.capabilities[key] ? "Configured" : "Not configured" :
					setup.capabilities[key] ? "Enabled" : "Not enabled"}</dd></div>)}</dl>
			<p className="restaurant-setup-hardware-note">Configured devices and printers are not verified as connected or working. Check the installed app and test hardware on site.</p>
		</>}
		<div className="restaurant-setup-activation">
			{(!setup || !checkoutEnabled(setup)) && <button type="button" disabled={busy || !canActivate(setup)} onClick={activate}>Activate checkout</button>}
			<p>{!setup ? "Load valid readiness before activation." : checkoutEnabled(setup) ? "Checkout is ready. No separate V2 activation is required." :
				setup.canActivateCheckout ? "Checkout is eligible for activation after confirmation." : "Resolve the checkout prerequisites, then refresh readiness."}</p>
		</div>
	</section>;
}

export default function RestaurantSetupReadiness({ restaurantId, onOpenSection }) {
	const [user, loading, error] = useAuthState(auth);
	const environment = getStaffProvisioningEnvironment();
	if (!environment) return null;
	if (loading) return <p role="status">Checking support access. Readiness unknown.</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account. Readiness unknown.</p>;
	if (!validId(restaurantId)) return <p role="alert">A valid restaurant is required. Readiness unknown.</p>;
	return <ReadinessStatus key={`${environment}:${restaurantId}:${user.uid}`} restaurantId={restaurantId} user={user}
		environment={environment} onOpenSection={onOpenSection} />;
}
